"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  IconBuilding,
  IconEye,
  IconEyeOff,
  IconLock,
  IconMail,
  IconMessage,
  IconUser,
} from "@tabler/icons-react";
import intlTelInput, { type Iti } from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

interface IIpApiData {
  "country_code": string;
}

interface ISignUpReponseData {
  "message"?: string;
  "error"?: string;
}

type TPasswordInput = "password" | "text";

/**
 * Sets layout and content for user registration page.
 *
 * @returns All elements of the page.
 */
export default function SignUp(): React.ReactElement {
  const router = useRouter();
  const [requestError, setRequestError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const intlTelInputObject = useRef<Iti | null>(null);
  const fullName = useRef<HTMLInputElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  const phoneNumber = useRef<HTMLInputElement>(null);
  const organization = useRef<HTMLInputElement>(null);
  const message = useRef<HTMLTextAreaElement>(null);

  const [passwordInputType, setPasswordInputType] =
    useState<TPasswordInput>("password");

  useEffect(() => {
    intlTelInputObject.current = intlTelInput(
      phoneNumber.current as unknown as HTMLInputElement,
      {
        "loadUtils": async () => import("intl-tel-input/utils"),
        "initialCountry": "auto",
        "nationalMode": false,
        "containerClass": "block",
        "countryOrder": ["dk"],
        "geoIpLookup": (success) => {
          fetch("https://ipapi.co/json")
            .then(async (res) => res.json())
            .then((data: IIpApiData) => {
              const countryCode = data.country_code.toLowerCase();
              success(countryCode as Parameters<typeof success>[0]);
            })
            .catch(() => {
              // Default to Denmark if geolocation fails
              success("dk");
            });
        },
      }
    );

    return () => {
      intlTelInputObject.current?.destroy();
    };
  }, []);

  /**
   * Toggles password visibility.
   */
  function togglePasswordVisibility(): void {
    if (passwordInputType === "text") {
      setPasswordInputType("password");
    } else {
      setPasswordInputType("text");
    }
  }

  return (
    <div className="container p-4 mx-auto max-w-[500px]">
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Sign Up
          </CardTitle>
          <CardDescription className="text-center">
            Take a look at how PhaseTree speeds up material discovery and get
            answers to your questions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event): void => {
              event.preventDefault();
              setRequestError(undefined);
              setIsLoading(true);

              fetch("/api/auth/sign-up", {
                "body": JSON.stringify({
                  "fullName": fullName.current?.value,
                  "email": email.current?.value,
                  "password": password.current?.value,
                  "organization": organization.current?.value,
                  "phoneNumber": intlTelInputObject.current?.getNumber(),
                  "message": message.current?.value,
                }),
                "method": "POST",
              })
                .then(async (res) => res.json())
                .then((data: ISignUpReponseData) => {
                  if (typeof data.error !== "undefined") {
                    setRequestError(data.error);
                    console.error(data.error);
                  } else {
                    router.push(
                      `/sign-up/verify-email?email=${encodeURIComponent(
                        email.current?.value ?? ""
                      )}&redirect=true`
                    );
                  }
                })
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => {
                  setIsLoading(false);
                });
            }}
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-full-name"
                  className="text-sm font-medium"
                >
                  Full Name
                </Label>
                <div className="relative">
                  <IconUser className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    className="pl-10"
                    autoComplete="name"
                    id="sign-up-full-name"
                    placeholder="Enter your full name"
                    ref={fullName}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-email"
                  className="text-sm font-medium"
                >
                  Email
                </Label>
                <div className="relative">
                  <IconMail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    className="pl-10"
                    id="sign-up-email"
                    placeholder="your@email.com"
                    autoComplete="email"
                    ref={email}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-password"
                  className="text-sm font-medium"
                >
                  Password
                </Label>
                <div className="relative">
                  <IconLock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={passwordInputType}
                    className="pl-10 pr-10"
                    id="sign-up-password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    aria-describedby="sign-up-password-requirements"
                    ref={password}
                    pattern="^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*[^\w\d\s]).{8,}$"
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 bg-transparent hover:bg-transparent"
                    onClick={togglePasswordVisibility}
                    disabled={isLoading}
                  >
                    {passwordInputType === "password" ? (
                      <IconEyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <IconEye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <p
                  id="sign-up-password-requirements"
                  className="text-xs text-muted-foreground"
                >
                  Password must be at least 8 characters in length and contain a
                  number, a special character, an uppercase and lowercase
                  letter.
                </p>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-organization"
                  className="text-sm font-medium"
                >
                  Organization
                </Label>
                <div className="relative">
                  <IconBuilding className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    className="pl-10"
                    id="sign-up-organization"
                    placeholder="Enter your organization"
                    autoComplete="organization"
                    ref={organization}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-phone-number"
                  className="text-sm font-medium"
                >
                  Phone Number
                </Label>
                <div className="relative">
                  <Input
                    type="tel"
                    className="pl-10"
                    id="sign-up-phone-number"
                    placeholder="Enter your phone number"
                    autoComplete="tel"
                    ref={phoneNumber}
                    onBlur={(event): void => {
                      if (
                        intlTelInputObject.current &&
                        !intlTelInputObject.current.isValidNumber()
                      ) {
                        event.target.setCustomValidity(
                          "Enter a valid phone number."
                        );
                      } else {
                        event.target.setCustomValidity("");
                      }
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="sign-up-message"
                  className="text-sm font-medium"
                >
                  Message
                </Label>
                <div className="relative">
                  <IconMessage className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Textarea
                    id="sign-up-message"
                    placeholder="(Optional) Tell us more about your needs..."
                    className="min-h-[100px] pl-10 pt-3"
                    ref={message}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {requestError !== undefined && requestError !== "" && (
                <Alert variant="destructive">
                  <AlertDescription>{requestError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Signing up..." : "Sign up"}
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    href="/log-in"
                    className="text-primary hover:text-primary/80 hover:underline"
                  >
                    Log in.
                  </Link>
                </p>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
